import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';

import { combineLatest, type Observable, of, Subject } from 'rxjs';
import {
  bufferWhen,
  catchError,
  concatAll,
  filter,
  map,
  mergeMap,
  shareReplay,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import {
  AnalyticsAdapter,
  AnalyticsConfig,
  ANALYTICS_ADAPTERS,
  ANALYTICS_DISABLED,
  AnalyticsClient,
} from '@core/analytics-types';
import type {
  AnalyticsAPIMethods,
  AnalyticsClientOptions,
  AnalyticsEventDescriptor,
  AnalyticsProps,
} from '@core/analytics-types';

// eslint-disable-next-line no-console
const log = (type: string, args: any[]) => console.info(`🙈 analytics::${type}`, args);

const debugClient$ = of<AnalyticsAPIMethods>({
  alias(...args: any[]) {
    log(`alias`, args);
  },
  identify(...args: any[]) {
    const callback = args.slice(-1).pop();
    log(`identify`, args);
    if (typeof callback === 'function') {
      callback();
    }
  },
  page(...args: any[]) {
    log(`page`, args);
  },
  track(...args: any[]) {
    log(`track`, args);
  },
  reset() {
    log(`reset`, []);
  },
});

@Injectable({ providedIn: 'root' })
export class AnalyticsBaseClient extends AnalyticsClient {
  #config = inject(AnalyticsConfig);
  #injector = inject(Injector);
  #adapters = inject<AnalyticsAdapter[]>(ANALYTICS_ADAPTERS);
  #analyticsDisabled? = inject(ANALYTICS_DISABLED, { optional: true });

  public readonly enabled =
    this.#analyticsDisabled !== true && (this.#config.isAvailable || this.#config.isDebug);

  public flushBuffer = new Subject<void>();
  public emitter$ = new Subject<AnalyticsEventDescriptor>();

  public buffered$ = this.emitter$.pipe(
    bufferWhen(() => this.flushBuffer.asObservable()),
    take(1),
    mergeMap(values =>
      of(
        ...values.sort(a => {
          if (a.method === 'identify') {
            return -1;
          }

          return 0;
        }),
      ),
    ),
  );

  private events$ = of(this.buffered$, this.emitter$).pipe(concatAll());
  private identify$ = this.emitter$.pipe(
    filter(evt => evt.method === 'identify'),
    take(1),
    shareReplay(1),
  );

  constructor() {
    super();

    if (this.enabled) {
      this.identify$.subscribe();
      const initialize$ = runInInjectionContext(this.#injector, () =>
        this.#config.initializeFactory(),
      );
      initialize$.pipe(take(1)).subscribe(enabled => {
        if (enabled) {
          this.install(this.#config);
        } else {
          this.#config.opts.debug = true;
          this.#config.opts.waitForIdentify = false;
          this.install(this.#config);
        }
      });
    }
  }

  public reset() {
    this.emitter$.next({
      data: {},
      options: {},
      method: 'reset',
    });
  }

  public track(name: string, props?: AnalyticsProps, options?: AnalyticsClientOptions) {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'track',
      data: {
        name,
        props,
      },
    });
  }

  public alias(id: number, options?: AnalyticsClientOptions): void {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'alias',
      data: {
        id,
      },
    });
  }

  public identify(
    userId?: number | string,
    traits?: Record<string, string | number | boolean>,
    options?: AnalyticsClientOptions,
  ): void {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'identify',
      data: {
        userId,
        traits,
      },
    });
  }

  public page(
    category: string,
    name: string,
    properties?: any,
    options?: AnalyticsClientOptions,
  ): void {
    this.emitter$.next({
      method: 'page',
      data: {
        category,
        name,
        properties: this.#config.pageTrackParamsSanitizer(name, category, properties),
      },
      options: this.getConfig(options),
    });
  }

  private install(config: AnalyticsConfig) {
    const client$ = !config.isDebug ? this.loadAdapters(config) : debugClient$;

    config.waitForIdentify
      ? this.runWithIdentifyRequired(client$)
      : this.runWithoutIdentifyRequired(client$);
  }

  private loadAdapters(config: AnalyticsConfig): Observable<AnalyticsAPIMethods> {
    const adapterClients$ = this.#adapters.map(adapter =>
      adapter.load(config.key, config.options).pipe(
        catchError(err => {
          console.warn('Analytics adapter failed to load', err);
          return of(null);
        }),
      ),
    );

    return combineLatest(adapterClients$).pipe(
      map(clients => {
        const loaded = clients.filter((c): c is AnalyticsAPIMethods => c !== null);
        return this.createMergedClient(loaded);
      }),
      shareReplay(1),
    );
  }

  private createMergedClient(clients: AnalyticsAPIMethods[]): AnalyticsAPIMethods {
    return {
      track: (eventName, eventProps, opts) =>
        clients.forEach(c => c.track(eventName, eventProps, opts)),
      identify: (userId, traits, opts, cb) => {
        const waitIdentify = clients.map(
          client =>
            new Promise(resolve => client.identify(userId, traits, opts, () => resolve(null))),
        );
        Promise.all(waitIdentify).then(() => cb?.());
      },
      page: (category, name, properties, opts) =>
        clients.forEach(c => c.page(category, name, properties, opts)),
      alias: id => clients.forEach(c => c.alias(id)),
      reset: () => clients.forEach(c => c.reset()),
    };
  }

  private runWithIdentifyRequired(client$: Observable<AnalyticsAPIMethods>) {
    this.attachClient(client$);

    combineLatest([this.identify$, client$])
      .pipe(take(1))
      .subscribe(([evt, client]) => {
        client.identify(evt.data.userId, evt.data.traits, evt.options, () =>
          this.flushBuffer.next(),
        );
      });
  }

  private runWithoutIdentifyRequired(client$: Observable<AnalyticsAPIMethods>) {
    this.attachClient(client$);
    client$.subscribe(() => this.flushBuffer.next());
  }

  private attachClient(client$: Observable<AnalyticsAPIMethods>) {
    this.events$.pipe(withLatestFrom(client$)).subscribe({
      next: ([evt, client]) => {
        // eslint-disable-next-line default-case
        switch (evt.method) {
          case 'reset':
            client.reset();
            break;
          case 'track':
            client.track(evt.data.name, evt.data.props || {}, evt.options);
            break;
          case 'identify':
            client.identify(evt.data.userId, evt.data.traits, evt.options);
            break;
          case 'alias':
            client.alias(evt.data.id);
            break;
          case 'page':
            client.page(evt.data.category, evt.data.name, evt.data.properties, evt.options);
            break;
        }
      },
    });
  }

  private getConfig(options: AnalyticsClientOptions = {}): AnalyticsClientOptions {
    return {
      ...this.#config.options,
      integrations: <AnalyticsClientOptions['integrations']>{
        ...this.#config.options.integrations,
        ...(options?.integrations || {}),
      },
    };
  }
}
